import { Button } from '@mantine/core';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { useLocalisedDate } from '../../../../utils';
import { Box } from '../../../shared/Box';
import { Text } from '../../../shared/Text';
import { useGetCoaSessionQuery } from './generated/DigitalJourneyView.generated';

export const DigitalJourneyView: React.FC<{ id: string }> = ({ id }) => {
	const { t } = useTranslation('EstimatingPortal');
	const { formatDate, formatHourAndMinutes } = useLocalisedDate();

	const { data: coaSessionData } = useGetCoaSessionQuery({ variables: { id } });

	const coaSession = coaSessionData?.coaSession || {};

	return coaSessionData && coaSession && coaSession.digitalJourney ? (
		<Box p="24px 24px 24px 24px" m="0" orientation="vertical" gap={8}>
			<DigitalJourneyInfoLine
				title={t('Completed at date')}
				value={coaSession.completedAt ? formatDate(coaSession.completedAt) : ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Registration number')}
				value={coaSession.digitalJourney?.licensePlatePh ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Vehicle type')}
				value={
					coaSession.digitalJourney.vehicleTypePh
						? t(`Vehicle ${coaSession.digitalJourney.vehicleTypePh.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Date of accident')}
				value={
					coaSession.digitalJourney?.accidentDateTime ? formatDate(coaSession.digitalJourney.accidentDateTime) : ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Time of accident')}
				value={
					coaSession.digitalJourney.accidentDateTime
						? formatHourAndMinutes(coaSession.digitalJourney.accidentDateTime)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Country of accident')}
				value={
					coaSession.digitalJourney?.accidentLocation?.country
						? t(coaSession.digitalJourney.accidentLocation.country)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Province of accident')}
				value={
					coaSession.digitalJourney?.accidentLocation?.province
						? t(coaSession.digitalJourney.accidentLocation.province)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('City of accident')}
				value={coaSession?.digitalJourney?.accidentLocation?.city ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Type of accident')}
				value={coaSession.digitalJourney?.accidentType ? t(coaSession.digitalJourney.accidentType.toLowerCase()) : ''}
			/>
			<DigitalJourneyInfoLine
				title={t('No of vehicles involved')}
				value={
					coaSession.digitalJourney?.numVehiclesInvolved !== null
						? t(`${coaSession.digitalJourney.numVehiclesInvolved} vehicles involved`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Presence of a driver in vehicle')}
				value={
					coaSession.digitalJourney &&
					'driverPresent' in coaSession.digitalJourney &&
					typeof coaSession.digitalJourney.driverPresent === 'boolean'
						? t(coaSession.digitalJourney.driverPresent ? 'Yes' : 'No')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Information on other vehicle')}
				value={
					coaSession.digitalJourney &&
					'informationOnOtherVehicle' in coaSession.digitalJourney &&
					typeof coaSession.digitalJourney.informationOnOtherVehicle === 'boolean'
						? t(coaSession.digitalJourney.informationOnOtherVehicle ? 'Yes' : 'No')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('No of injured people')}
				value={
					coaSession.digitalJourney &&
					coaSession.digitalJourney.numInjuredPeople !== null &&
					!Number.isNaN(coaSession.digitalJourney.numInjuredPeople)
						? coaSession.digitalJourney.numInjuredPeople.toString()
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('CAI')}
				value={coaSession.digitalJourney?.cai ? t(`CAI ${coaSession.digitalJourney.cai.toLowerCase()}`) : ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Authorities intervention')}
				value={
					coaSession.digitalJourney?.authoritiesIntervention
						? t(`Authorities ${coaSession.digitalJourney.authoritiesIntervention.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle circumstance')}
				value={
					coaSession.digitalJourney?.vehicleCircumstancePh
						? t(`Circumstance ${coaSession.digitalJourney.vehicleCircumstancePh.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle circumstance')}
				value={
					coaSession.digitalJourney?.otherVehicleCircumstance
						? t(`Circumstance ${coaSession.digitalJourney.otherVehicleCircumstance.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Vehicle mileage')}
				value={
					coaSession.digitalJourney &&
					coaSession.digitalJourney.mileage !== null &&
					!Number.isNaN(coaSession.digitalJourney.mileage)
						? coaSession.digitalJourney.mileage.toString()
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Airbag deployed')}
				value={
					coaSession.digitalJourney &&
					'airbagDeployed' in coaSession.digitalJourney &&
					typeof coaSession.digitalJourney.airbagDeployed === 'boolean'
						? t(coaSession.digitalJourney.airbagDeployed ? 'Yes' : 'No')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Vehicle drivable')}
				value={
					coaSession.digitalJourney &&
					'isDrivable' in coaSession.digitalJourney &&
					typeof coaSession.digitalJourney.isDrivable === 'boolean'
						? t(coaSession.digitalJourney.isDrivable ? 'Yes' : 'No')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle impact location')}
				value={
					coaSession.digitalJourney?.pointsOfImpactPh && coaSession.digitalJourney.pointsOfImpactPh.length > 0
						? coaSession.digitalJourney.pointsOfImpactPh
								.map((pointOfImpact) => t(`Impact ${pointOfImpact.toLowerCase()}`))
								.join(', ')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle driver firstname')}
				value={coaSession.digitalJourney?.firstNamePh ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle driver lastname')}
				value={coaSession.digitalJourney?.lastNamePh ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle driver personal code')}
				value={coaSession.digitalJourney?.personalCodePh ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle registration number')}
				value={coaSession.digitalJourney?.otherVehicleLicensePlate ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle type')}
				value={
					coaSession.digitalJourney?.otherVehicleType
						? t(`Vehicle ${coaSession.digitalJourney.otherVehicleType.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle insurance')}
				value={coaSession.digitalJourney?.otherVehicleInsurance ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle driver firstname')}
				value={coaSession.digitalJourney?.otherDriverFirstName ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle driver lastname')}
				value={coaSession.digitalJourney?.otherDriverLastName ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle driver personal code')}
				value={coaSession.digitalJourney?.otherDriverPersonalCode ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Other vehicle impact location')}
				value={
					coaSession.digitalJourney?.otherVehiclePointsOfImpact &&
					coaSession.digitalJourney.otherVehiclePointsOfImpact.length > 0
						? coaSession.digitalJourney.otherVehiclePointsOfImpact
								.map((pointOfImpact) => t(`Impact ${pointOfImpact.toLowerCase()}`))
								.join(', ')
						: ''
				}
			/>
			{new Array(5).fill(undefined).map((_, index) => (
				<>
					<DigitalJourneyInfoLine
						title={`${t('Injured person firstname')} ${index + 1}`}
						value={
							coaSession.digitalJourney?.injuredPersons && coaSession.digitalJourney.injuredPersons[index]
								? coaSession.digitalJourney.injuredPersons[index].firstName
								: ''
						}
					/>
					<DigitalJourneyInfoLine
						title={`${t('Injured person lastname')} ${index + 1}`}
						value={
							coaSession.digitalJourney?.injuredPersons && coaSession.digitalJourney.injuredPersons[index]
								? coaSession.digitalJourney.injuredPersons[index].lastName
								: ''
						}
					/>
					<DigitalJourneyInfoLine
						title={`${t('Injured person personal code')} ${index + 1}`}
						value={
							coaSession.digitalJourney?.injuredPersons &&
							coaSession.digitalJourney.injuredPersons[index] &&
							coaSession.digitalJourney?.injuredPersons[index].personalCode
								? coaSession.digitalJourney.injuredPersons[index].personalCode
								: ''
						}
					/>
					<DigitalJourneyInfoLine
						title={`${t('Injured person role')} ${index + 1}`}
						value={
							coaSession.digitalJourney?.injuredPersons &&
							coaSession.digitalJourney.injuredPersons[index] &&
							coaSession.digitalJourney.injuredPersons[index].role
								? t(coaSession.digitalJourney.injuredPersons[index].role.toLowerCase())
								: ''
						}
					/>
				</>
			))}
			<DigitalJourneyInfoLine
				title={t('Accident additional details')}
				value={coaSession.digitalJourney?.additionalDetails ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Claim handling decision')}
				value={
					coaSession.digitalJourney?.claimHandlingDecision
						? t(`Claim decision ${coaSession.digitalJourney.claimHandlingDecision.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Insured vehicle driver postcode')}
				value={coaSession.digitalJourney?.postalCodePh ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Chosen bodyshop name')}
				value={coaSession.digitalJourney?.bodyShop?.name ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Chosen bodyshop address')}
				value={coaSession.digitalJourney?.bodyShop?.address ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Chosen bodyshop postcode')}
				value={coaSession.digitalJourney?.bodyShop?.postalCode ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Chosen bodyshop city')}
				value={coaSession.digitalJourney?.bodyShop?.city ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Chosen bodyshop VAT number')}
				value={coaSession.digitalJourney?.bodyShop?.vatNumber ?? ''}
			/>
			<DigitalJourneyInfoLine
				title={t('Vandalism severity')}
				value={
					coaSession.digitalJourney?.vandalismSeverity
						? t(`Severity ${coaSession.digitalJourney.vandalismSeverity.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Damaged glass point of impact')}
				value={
					coaSession.digitalJourney?.pointsOfImpactGlass
						? coaSession.digitalJourney.pointsOfImpactGlass
								.map((pointOfImpactGlass) => t(`Glass impact ${pointOfImpactGlass.toLowerCase()}`))
								.join(', ')
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Event type')}
				value={
					coaSession.digitalJourney?.naturalEventType
						? t(`Event type ${coaSession.digitalJourney.naturalEventType.toLowerCase()}`)
						: ''
				}
			/>
			<DigitalJourneyInfoLine
				title={t('Warranty type')}
				value={
					coaSession.digitalJourney?.insuranceType
						? t(`Warranty type ${coaSession.digitalJourney.insuranceType.toLowerCase()}`)
						: ''
				}
			/>
			{coaSession.digitalJourney?.pdfS3Location && (
				<Box m="16px 0 0 0">
					<a
						href={coaSession.digitalJourney.pdfS3Location}
						download
						target="_blank"
						rel="noopener noreferrer"
						style={{ textDecoration: 'none' }}
					>
						<Button
							styles={(theme) => ({
								root: {
									backgroundColor: '#3b25c4',
									'&:hover': {
										backgroundColor: theme.fn.darken('#3b25c4', 0.05),
									},
								},
							})}
						>
							{t('Download Digital Journey PDF')}
						</Button>
					</a>
				</Box>
			)}
		</Box>
	) : null;
};

const DigitalJourneyInfoLine: React.FC<{ title: string; value: string }> = ({ title, value }) => {
	return (
		<Box orientation="horizontal" gap={24} justifyContent="space-between">
			<Text typography="small">{title}</Text>
			<Text typography="small">{value}</Text>
		</Box>
	);
};
